import { actorInvoiceMatch, type ActorBilling } from './invoicing.js';

/**
 * Project costing (owner 2026-10-29): the hours a project took per role, times what an hour costs KFF and what it is worth
 * to the client, from the time matrix when nobody logged time. "Default ska vara snittpris som kan justeras men som sätts
 * till 300kr/h. Sedan kan admin justera detta på personen": one average rate per role, overridden per person. The matrix
 * has edit and camera (seeded from Monday's Hours Matrix) and coordinator (owner: 4/8/12 for Bergman/Spielberg/Hitchcock).
 * Actors are paid on invoice, so a signed model release is compared with the supplier invoice from the actor.
 */

export const COST_ROLES = ['edit', 'camera', 'coordinator'] as const;
export type CostRole = (typeof COST_ROLES)[number];

/** Planned hours for one project of a package. */
export interface CostMatrixRow {
  /** first draft, for the whole project */
  editFirstH: number;
  /** each client revision round */
  editRoundH: number;
  cameraOnSiteH: number;
  cameraPrepH: number;
  cameraTravelH: number;
  coordinatorH: number;
}

export interface CostingSettings {
  /** what an hour costs KFF, the average per role (kr excl. VAT) */
  rates: Record<CostRole, number>;
  /** what an hour is worth to the client (the price of an extra revision round's hour) */
  billRate: number;
  /** client revision rounds counted in the plan */
  rounds: number;
  /** person id → their own internal rate, overriding the role average */
  personRates: Record<string, number>;
  /** package key → planned hours */
  matrix: Record<string, CostMatrixRow>;
}

const row = (editFirstH: number, editRoundH: number, cameraOnSiteH: number, cameraPrepH: number, cameraTravelH: number, coordinatorH: number): CostMatrixRow => ({
  editFirstH,
  editRoundH,
  cameraOnSiteH,
  cameraPrepH,
  cameraTravelH,
  coordinatorH
});

/** Edit and camera as Monday's Hours Matrix has them (Kubrick, Scott and Lucas are not there: set from the price list). */
export const DEFAULT_COSTING: CostingSettings = {
  rates: { edit: 300, camera: 300, coordinator: 300 },
  billRate: 1500,
  rounds: 2,
  personRates: {},
  matrix: {
    Bergman: row(8, 2, 2, 0.5, 2, 4),
    Spielberg: row(12, 3, 4, 0.5, 2, 8),
    Hitchcock: row(24, 4, 8, 0.5, 2, 12),
    Kubrick: row(40, 5, 16, 1, 4, 16),
    Scott: row(10, 2, 3, 0.5, 2, 6),
    Akerman: row(12, 3, 2, 0.5, 2, 8),
    Lucas: row(8, 2, 0, 0, 0, 2),
    Jones: row(20, 3, 0, 0, 0, 4)
  }
};

/** The settings as saved, filled out with the defaults (a package or rate added later is never missing). */
export function costingOf(
  saved: Partial<CostingSettings> | null | undefined,
  monday?: {
    packages: { name: string; firstDraftH: number; revisionH: number }[];
    photographerTime: {
      name: string;
      onSiteH: number;
      prepH: number;
      travelH: number;
    }[];
  } | null
): CostingSettings {
  const matrix: Record<string, CostMatrixRow> = {};
  for (const [k, d] of Object.entries(DEFAULT_COSTING.matrix)) {
    // never saved: Monday's matrix as it is today
    const ed = monday?.packages.find((p) => p.name.toLowerCase() === k.toLowerCase());
    const ph = monday?.photographerTime.find((p) => p.name.toLowerCase() === k.toLowerCase());
    const seeded: CostMatrixRow = {
      ...d,
      ...(ed ? { editFirstH: ed.firstDraftH, editRoundH: ed.revisionH } : {}),
      ...(ph
        ? {
            cameraOnSiteH: ph.onSiteH,
            cameraPrepH: ph.prepH,
            cameraTravelH: ph.travelH
          }
        : {})
    };
    matrix[k] = { ...seeded, ...(saved?.matrix?.[k] ?? {}) };
  }
  return {
    rates: { ...DEFAULT_COSTING.rates, ...(saved?.rates ?? {}) },
    billRate: saved?.billRate ?? DEFAULT_COSTING.billRate,
    rounds: saved?.rounds ?? DEFAULT_COSTING.rounds,
    personRates: { ...(saved?.personRates ?? {}) },
    matrix
  };
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const r0 = (n: number) => Math.round(n);

/** The matrix row for a package; unknown package = Bergman. */
export function matrixRow(c: CostingSettings, packageKey: string | null): CostMatrixRow {
  return c.matrix[packageKey ?? ''] ?? c.matrix[Object.keys(c.matrix).find((k) => k.toLowerCase() === (packageKey ?? '').toLowerCase()) ?? 'Bergman'] ?? DEFAULT_COSTING.matrix.Bergman!;
}

/** Planned hours per role for a project; camera only when the project has a shoot; unknown package = Bergman. */
export function plannedHours(c: CostingSettings, packageKey: string | null, shoot: boolean): Record<CostRole, number> {
  const m = matrixRow(c, packageKey);
  return {
    edit: r1(m.editFirstH + m.editRoundH * c.rounds),
    camera: shoot ? r1(m.cameraOnSiteH + m.cameraPrepH + m.cameraTravelH) : 0,
    coordinator: r1(m.coordinatorH)
  };
}

/** Logged time on the project, already given its role. */
export interface CostEntry {
  personId: string;
  role: CostRole;
  minutes: number;
  /** a freelancer's cost is their invoice, not hours × rate */
  freelancer: boolean;
}

export interface RoleCosting {
  role: CostRole;
  plannedH: number;
  loggedH: number;
  /** logged when there is any, otherwise planned */
  countedH: number;
  source: 'logged' | 'planned' | 'none';
  /** hours by freelancers (in loggedH, costed by their invoice) */
  freelancerH: number;
  /** planned hours of a role only freelancers have on the project: their invoice is the cost */
  byInvoice: boolean;
  /** KFF's own cost for the hours */
  cost: number;
  /** what the hours are worth to the client */
  value: number;
}

export interface ActorCosting {
  castingId: string;
  name: string;
  role: string | null;
  /** the fee in the signed model release, excl. VAT */
  agreed: number;
  /** what the actor's invoices tied to the project add up to, excl. VAT; null = no invoice yet */
  invoiced: number | null;
  invoiceIds: string[];
  /** invoiced − agreed */
  diff: number | null;
}

export interface ProjectCosting {
  roles: RoleCosting[];
  countedH: number;
  /** hours × internal rate (freelancers excluded) */
  timeCost: number;
  /** counted hours × the bill rate */
  value: number;
  actors: ActorCosting[];
  /** signed fees with no invoice yet: a cost still to come */
  actorsExpected: number;
  /** what the project brings in: invoiced, or the package's list price when nothing is invoiced yet */
  revenue: number | null;
  revenueFrom: 'invoiced' | 'list' | null;
  /** the hours are worth more than the project brings in */
  over: { kr: number; h: number } | null;
}

const ROLE_ORDER: Record<CostRole, number> = {
  edit: 0,
  camera: 1,
  coordinator: 2
};

/** Matches signed model releases to the actors' invoices on the project (supplier name = the actor's name or company). */
export function actorCosting(
  castings: (ActorBilling & { id: string; role: string | null; fee: number | null })[],
  invoices: { id: string; supplierName: string | null; supplierOrgNo?: string | null; number?: string | null; texts?: string[]; net: number }[]
): ActorCosting[] {
  const used = new Set<string>();
  return castings.map((c) => {
    const mine = invoices.filter((i) => !used.has(i.id) && actorInvoiceMatch(i, c) !== null);
    for (const i of mine) used.add(i.id);
    const agreed = c.fee ?? 0;
    const invoiced = mine.length ? Math.round(mine.reduce((s, i) => s + i.net, 0) * 100) / 100 : null;
    return {
      castingId: c.id,
      name: c.name,
      role: c.role,
      agreed,
      invoiced,
      invoiceIds: mine.map((i) => i.id),
      diff: invoiced === null ? null : Math.round((invoiced - agreed) * 100) / 100
    };
  });
}

export function projectCosting(input: {
  settings: CostingSettings;
  packageKey: string | null;
  shoot: boolean;
  entries: CostEntry[];
  /** roles everyone on the project in is a freelancer: their planned hours cost KFF nothing here (their invoice does) */
  freelancedRoles?: CostRole[];
  actors: ActorCosting[];
  /** excl. VAT: invoice-basis lines on the project that reached Bokio */
  invoiced: number;
  /** the package's list price for one project, when known */
  listPrice: number | null;
}): ProjectCosting {
  const c = input.settings;
  const planned = plannedHours(c, input.packageKey, input.shoot);
  const roles = COST_ROLES.map((role): RoleCosting => {
    const mine = input.entries.filter((e) => e.role === role);
    const loggedH = r1(mine.reduce((s, e) => s + e.minutes, 0) / 60);
    const freelancerH = r1(mine.filter((e) => e.freelancer).reduce((s, e) => s + e.minutes, 0) / 60);
    const source = loggedH > 0 ? 'logged' : planned[role] > 0 ? 'planned' : 'none';
    const countedH = source === 'logged' ? loggedH : planned[role];
    const byInvoice = source === 'planned' && (input.freelancedRoles ?? []).includes(role);
    const cost =
      source === 'logged' ? mine.filter((e) => !e.freelancer).reduce((s, e) => s + (e.minutes / 60) * (c.personRates[e.personId] ?? c.rates[role]), 0) : byInvoice ? 0 : countedH * c.rates[role];
    return {
      role,
      plannedH: planned[role],
      loggedH,
      countedH,
      source,
      freelancerH,
      byInvoice,
      cost: r0(cost),
      value: r0(countedH * c.billRate)
    };
  }).sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]);
  const countedH = r1(roles.reduce((s, r) => s + r.countedH, 0));
  const value = r0(roles.reduce((s, r) => s + r.value, 0));
  const revenue = input.invoiced > 0 ? input.invoiced : input.listPrice;
  const revenueFrom = input.invoiced > 0 ? 'invoiced' : input.listPrice !== null ? 'list' : null;
  const over = revenue !== null && revenue > 0 && value > revenue ? { kr: r0(value - revenue), h: r1((value - revenue) / c.billRate) } : null;
  return {
    roles,
    countedH,
    timeCost: r0(roles.reduce((s, r) => s + r.cost, 0)),
    value,
    actors: input.actors,
    actorsExpected: r0(input.actors.filter((a) => a.invoiced === null).reduce((s, a) => s + a.agreed, 0)),
    revenue,
    revenueFrom,
    over
  };
}

/**
 * The role an hour counts for: editing, shoot and producer time by its kind; review and other time by the person's role on
 * the project (editor → edit, camera → camera, producer or CD → coordinator); coordinator otherwise.
 */
export function costRoleOf(memberRoles: string[], kind: string): CostRole {
  if (kind === 'editing') return 'edit';
  if (kind === 'shoot') return 'camera';
  if (kind === 'producer') return 'coordinator';
  const roles = memberRoles.map((r): CostRole => (r === 'editor' ? 'edit' : r === 'camera' ? 'camera' : 'coordinator')).sort((a, b) => ROLE_ORDER[a] - ROLE_ORDER[b]);
  return roles[0] ?? 'coordinator';
}
